/**
 * GitHub REST API client
 */

import { IntegrationError } from '../../types';
import type {
  GitHubCreateRepoParams,
  GitHubRepoCreated,
  GitHubCreatePRParams,
  GitHubGetPRCommentsParams,
  GitHubListIssuesParams,
  GitHubGetIssueParams,
  GitHubPullRequest,
  GitHubIssue,
  GitHubComment,
  GitHubRateLimit,
} from './types';

/**
 * GitHub API client configuration
 */
export interface GitHubAPIConfig {
  token: string;
  owner: string;
  repo: string;
}

/**
 * GitHub API response
 */
interface GitHubAPIResponse<T> {
  data: T;
  rateLimit: GitHubRateLimit;
}

/**
 * Execute a GitHub API request
 */
async function githubFetch<T>(
  endpoint: string,
  config: GitHubAPIConfig,
  options: RequestInit = {}
): Promise<GitHubAPIResponse<T>> {
  const url = `https://api.github.com${endpoint}`;

  const headers = {
    'Authorization': `Bearer ${config.token}`,
    'Accept': 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Extract rate limit info
    const rateLimit: GitHubRateLimit = {
      remaining: parseInt(response.headers.get('x-ratelimit-remaining') || '0', 10),
      limit: parseInt(response.headers.get('x-ratelimit-limit') || '5000', 10),
      reset: parseInt(response.headers.get('x-ratelimit-reset') || '0', 10),
    };

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));

      // Handle rate limiting
      if (response.status === 429) {
        throw new IntegrationError(
          'GitHub API rate limit exceeded',
          'RATE_LIMIT_ERROR',
          { rateLimit, error }
        );
      }

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        throw new IntegrationError(
          `GitHub API authentication failed: ${error.message || response.statusText}`,
          'AUTH_ERROR',
          { status: response.status, error }
        );
      }

      throw new IntegrationError(
        `GitHub API request failed: ${error.message || response.statusText}`,
        'SERVICE_ERROR',
        { status: response.status, error }
      );
    }

    const data = await response.json();

    return { data, rateLimit };
  } catch (error) {
    if (error instanceof IntegrationError) {
      throw error;
    }

    throw new IntegrationError(
      `GitHub API request failed: ${error instanceof Error ? error.message : String(error)}`,
      'NETWORK_ERROR',
      { error }
    );
  }
}

/**
 * GAP-104/105: Create a new repository for the authenticated user.
 * The token must have the `repo` scope.
 */
export async function createRepo(
  params: GitHubCreateRepoParams,
  token: string,
): Promise<GitHubRepoCreated> {
  const { name, isPrivate = true, description, autoInit = true } = params;

  const response = await fetch('https://api.github.com/user/repos', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
    },
    body: JSON.stringify({
      name,
      private: isPrivate,
      description: description ?? 'Generated by Almadar Studio',
      auto_init: autoInit,
    }),
  });

  if (!response.ok) {
    const err = await response.text();
    throw new IntegrationError(
      `Failed to create GitHub repository "${name}": ${response.status} ${err}`,
      'SERVICE_ERROR',
      { status: response.status },
    );
  }

  const repo = await response.json() as {
    clone_url: string;
    owner: { login: string };
    name: string;
    default_branch: string;
    full_name: string;
  };

  return {
    repoUrl: repo.clone_url,
    owner: repo.owner.login,
    repo: repo.name,
    defaultBranch: repo.default_branch,
    fullName: repo.full_name,
  };
}

/**
 * GAP-104/105: List the file tree of a repository using the Git Trees API.
 * Returns a flat list of all files in one request (recursive=1).
 */
export async function listRepoTree(
  config: GitHubAPIConfig,
  branch?: string,
): Promise<Array<{ path: string; type: 'blob' | 'tree'; size?: number }>> {
  const ref = branch ?? 'main';
  const { data } = await githubFetch<{ tree: Array<{ path: string; type: string; size?: number }> }>(
    `/repos/${config.owner}/${config.repo}/git/trees/${ref}?recursive=1`,
    config,
  );
  return data.tree.map(entry => ({
    path: entry.path,
    type: entry.type as 'blob' | 'tree',
    size: entry.size,
  }));
}

/**
 * GAP-104/105: Read a single file's content from a repository.
 */
export async function readRepoFile(
  config: GitHubAPIConfig,
  filePath: string,
  branch?: string,
): Promise<string | null> {
  const ref = branch ? `?ref=${branch}` : '';
  try {
    const response = await fetch(
      `https://api.github.com/repos/${config.owner}/${config.repo}/contents/${filePath}${ref}`,
      {
        headers: {
          'Authorization': `Bearer ${config.token}`,
          'Accept': 'application/vnd.github.raw+json',
          'X-GitHub-Api-Version': '2022-11-28',
        },
      },
    );
    if (!response.ok) return null;
    return await response.text();
  } catch {
    return null;
  }
}

/**
 * Create a pull request
 */
export async function createPR(
  params: GitHubCreatePRParams,
  config: GitHubAPIConfig
): Promise<GitHubPullRequest> {
  const { title, body, baseBranch, headBranch, draft = false } = params;

  const endpoint = `/repos/${config.owner}/${config.repo}/pulls`;

  const { data } = await githubFetch<GitHubPullRequest>(endpoint, config, {
    method: 'POST',
    body: JSON.stringify({
      title,
      body,
      base: baseBranch,
      head: headBranch,
      draft,
    }),
  });

  return data;
}

/**
 * Get pull request comments
 */
export async function getPRComments(
  params: GitHubGetPRCommentsParams,
  config: GitHubAPIConfig
): Promise<GitHubComment[]> {
  const { prNumber } = params;

  const endpoint = `/repos/${config.owner}/${config.repo}/pulls/${prNumber}/comments`;

  const { data } = await githubFetch<GitHubComment[]>(endpoint, config);

  return data;
}

/**
 * List repository issues
 */
export async function listIssues(
  params: GitHubListIssuesParams,
  config: GitHubAPIConfig
): Promise<GitHubIssue[]> {
  const { state = 'open', labels = [], limit = 30 } = params;

  const queryParams = new URLSearchParams({
    state,
    per_page: Math.min(limit, 100).toString(),
  });

  if (labels.length > 0) {
    queryParams.append('labels', labels.join(','));
  }

  const endpoint = `/repos/${config.owner}/${config.repo}/issues?${queryParams}`;

  const { data } = await githubFetch<GitHubIssue[]>(endpoint, config);

  return data;
}

/**
 * Get issue details
 */
export async function getIssue(
  params: GitHubGetIssueParams,
  config: GitHubAPIConfig
): Promise<{ issue: GitHubIssue; comments: GitHubComment[] }> {
  const { issueNumber } = params;

  // Get issue details
  const issueEndpoint = `/repos/${config.owner}/${config.repo}/issues/${issueNumber}`;
  const { data: issue } = await githubFetch<GitHubIssue>(issueEndpoint, config);

  // Get issue comments
  const commentsEndpoint = `/repos/${config.owner}/${config.repo}/issues/${issueNumber}/comments`;
  const { data: comments } = await githubFetch<GitHubComment[]>(commentsEndpoint, config);

  return { issue, comments };
}

/**
 * Get current rate limit status
 */
export async function getRateLimit(config: GitHubAPIConfig): Promise<GitHubRateLimit> {
  const endpoint = '/rate_limit';

  const { data } = await githubFetch<{ rate: GitHubRateLimit }>(endpoint, config);

  return data.rate;
}

// ============================================================================
// Git History API
// ============================================================================

/**
 * A commit from the GitHub API with optional stats.
 */
export interface GitHubCommit {
  sha: string;
  message: string;
  author: { name: string; email: string; date: string };
  stats?: { additions: number; deletions: number; total: number };
}

/**
 * List commits for a repository, optionally filtered by file path.
 */
export async function listCommits(
  config: GitHubAPIConfig,
  options?: { path?: string; perPage?: number; page?: number },
): Promise<GitHubCommit[]> {
  const params = new URLSearchParams();
  if (options?.path) params.set('path', options.path);
  if (options?.perPage) params.set('per_page', options.perPage.toString());
  if (options?.page) params.set('page', options.page.toString());

  const queryStr = params.toString();
  const endpoint = `/repos/${config.owner}/${config.repo}/commits${queryStr ? `?${queryStr}` : ''}`;

  const { data } = await githubFetch<Array<{
    sha: string;
    commit: {
      message: string;
      author: { name: string; email: string; date: string };
    };
    stats?: { additions: number; deletions: number; total: number };
  }>>(endpoint, config);

  return data.map((item) => ({
    sha: item.sha,
    message: item.commit.message,
    author: item.commit.author,
    stats: item.stats,
  }));
}

/**
 * Get the diff for a specific commit as a unified diff string.
 */
export async function getCommitDiff(
  config: GitHubAPIConfig,
  sha: string,
): Promise<string> {
  const url = `https://api.github.com/repos/${config.owner}/${config.repo}/commits/${sha}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${config.token}`,
      Accept: 'application/vnd.github.diff',
      'X-GitHub-Api-Version': '2022-11-28',
    },
  });

  if (!response.ok) {
    throw new IntegrationError(
      `GitHub API request failed: ${response.statusText}`,
      'SERVICE_ERROR',
      { status: response.status },
    );
  }

  return response.text();
}

/**
 * Get a file's content at a specific commit SHA.
 * Returns null if the file doesn't exist at that commit.
 */
export async function getFileAtCommit(
  config: GitHubAPIConfig,
  path: string,
  sha: string,
): Promise<string | null> {
  const endpoint = `/repos/${config.owner}/${config.repo}/contents/${path}?ref=${sha}`;

  try {
    const response = await fetch(`https://api.github.com${endpoint}`, {
      headers: {
        Authorization: `Bearer ${config.token}`,
        Accept: 'application/vnd.github.raw+json',
        'X-GitHub-Api-Version': '2022-11-28',
      },
    });

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`${response.status}: ${response.statusText}`);
    }

    return response.text();
  } catch (error) {
    if (error instanceof IntegrationError) throw error;
    throw new IntegrationError(
      `Failed to get file at commit: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { path, sha, error },
    );
  }
}

/**
 * Parse repository owner and name from URL
 */
export function parseRepoUrl(repoUrl: string): { owner: string; repo: string } {
  try {
    const url = new URL(repoUrl);
    const pathParts = url.pathname.split('/').filter(Boolean);

    if (pathParts.length < 2) {
      throw new Error('Invalid repository URL format');
    }

    const owner = pathParts[0];
    const repo = pathParts[1].replace(/\.git$/, '');

    return { owner, repo };
  } catch (error) {
    throw new IntegrationError(
      `Failed to parse repository URL: ${repoUrl}`,
      'VALIDATION_ERROR',
      { error }
    );
  }
}
